import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule, ModuleRegistry, ICellRendererParams, IGetRowsParams } from 'ag-grid-community';
import AddSeasonModal from '../../components/modals/AddSeasonModal';
import ConfirmDialog from '../../components/modals/ConfirmDialog';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { formatLocalDate } from '../../utils/dateUtils';
import SeasonAnalyticsCue from '../../components/SeasonAnalyticsCue';
import { PermissionGuard } from '../../components/common/PermissionGuard';
// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface SeasonData {
  _id?: string;
  name: string;
  startDate: string;
  endDate: string;
  shotClock: boolean;
  halfPeriod?: boolean;
  active: boolean;
  isTournament?: boolean;
  [key: string]: any;
}

const Seasons: React.FC = () => {
  const clientIdFromSession = sessionManager.getClientId();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [seasonToDelete, setSeasonToDelete] = useState<SeasonData | null>(null);
  const [editSeason, setEditSeason] = useState<SeasonData | null>(null);
  const [pageSize, setPageSize] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const gridApi = useRef<any>(null);

  // Define the datasource outside of onGridReady
  const datasource = useMemo(
    () => ({
      getRows: async (getRowsParams: IGetRowsParams) => {
        const page = Math.floor(getRowsParams.startRow / pageSize);
        const limit = getRowsParams.endRow - getRowsParams.startRow;
        if (getRowsParams.startRow === 0) setIsLoading(true);

        try {
          const params: any = {
            page: page + 1,
            limit: limit,
            clientId: clientIdFromSession,
          };

          const response = await api.get(API_URLS.GET_SEASONS, { params });
          if (response.data.success) {
            getRowsParams.successCallback(response.data.data, response.data.total);
            if (getRowsParams.startRow === 0) setIsLoading(false);
          } else {
            showErrorMsg(response.data.message || 'Failed to fetch seasons');
            getRowsParams.failCallback();
            setIsLoading(false);
          }
        } catch (error) {
          showErrorMsg('Failed to fetch seasons.');
          getRowsParams.failCallback();
          setIsLoading(false);
        }
      },
    }),
    [pageSize, clientIdFromSession],
  );

  // On grid ready, just save the api ref
  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    setIsLoading(true);
  };

  // Handle pagination changes (including page size changes)
  const onPaginationChanged = (params?: any) => {
    if (!gridApi.current) return;
    const newPageSize = gridApi.current.paginationGetPageSize();
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Edit button logic
  const handleEditClick = (season: SeasonData) => {
    setEditSeason(season);
    setIsModalOpen(true);
  };

  // Delete button logic
  const handleDeleteClick = (season: SeasonData) => {
    setSeasonToDelete(season);
    setIsDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (seasonToDelete && seasonToDelete._id) {
      try {
        const response = await api.delete(API_URLS.DELETE_SEASON, {
          params: { _id: seasonToDelete._id },
        });
        if (response.data.success) {
          showSuccessMsg('Season deleted successfully!');
          if (gridApi.current) {
            gridApi.current.purgeInfiniteCache();
          }
        } else {
          showErrorMsg(response.data.message || 'Failed to delete season');
        }
      } catch (error) {
        showErrorMsg('Failed to delete season.');
      }
    }
    setIsDeleteDialogOpen(false);
    setSeasonToDelete(null);
  };

  // Only refresh grid after add or update
  const handleSeasonAddedOrUpdated = () => {
    setIsModalOpen(false);
    setEditSeason(null);
    if (gridApi.current) {
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Only close modal, do not refresh grid
  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditSeason(null);
  };

  // Handle row click to navigate to stats page
  const handleRowClick = (event: any) => {
    const target = event.event?.target;
    if (target) {
      if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('.ag-cell[col-id="5"]')) {
        return;
      }
    }

    const seasonData = event.data;
    if (seasonData && seasonData._id) {
      sessionManager.setSeasonId(seasonData._id);
      sessionManager.setSeasonName(seasonData.name);
      sessionManager.setStartDate(seasonData.startDate);
      sessionManager.setEndDate(seasonData.endDate);

      // Always redirect to teams for both mobile and web
      navigate(`/seasons/${seasonData._id}/teams`, {
        state: { seasonId: seasonData._id, seasonData },
      });
    }
  };

  const NameCellRenderer: React.FC<ICellRendererParams<SeasonData>> = ({ value }) => {
    return (
      <div className="flex h-full w-full items-center gap-3">
        <span className="truncate font-medium">{value}</span>
        <SeasonAnalyticsCue />
      </div>
    );
  };

  const StatusCellRenderer: React.FC<ICellRendererParams<SeasonData>> = ({ value }) => {
    return (
      <span className={`px-2 py-1 rounded-full ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {value ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const ShotClockCellRenderer: React.FC<ICellRendererParams<SeasonData>> = ({ value }) => {
    return (
      <span className={`px-2 py-1 rounded-full ${value ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
        {value ? 'Yes' : 'No'}
      </span>
    );
  };

  const TypeCellRenderer: React.FC<ICellRendererParams<SeasonData>> = ({ data }) => {
    const isTournament = data?.isTournament === true;
    return (
      <span
        className={`px-2 py-1 rounded-full ${isTournament ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}
      >
        {isTournament ? 'Tournament' : 'Season'}
      </span>
    );
  };

  const PeriodCellRenderer: React.FC<ICellRendererParams<SeasonData>> = ({ data }) => {
    const hp = data?.halfPeriod === true;
    return <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800">{hp ? 'Halves' : 'Quarters'}</span>;
  };

  const DateCellRenderer: React.FC<ICellRendererParams<SeasonData>> = ({ value }) => {
    if (!value) return <span>-</span>;
    return <span>{formatLocalDate(value)}</span>;
  };

  const ActionsCellRenderer: React.FC<ICellRendererParams<SeasonData>> = ({ data }) => {
    if (!data) return null;

    const canEdit = sessionManager.canPerformAction('seasons', 'edit');
    const canDelete = sessionManager.canPerformAction('seasons', 'delete');
    const allowMutations = !data.isTournament;

    return (
      <div className="flex items-center justify-center space-x-2">
        {canEdit && allowMutations && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(data);
            }}
            className="text-blue-600 hover:text-blue-800 transform hover:scale-110 transition-all"
            title="Edit Season"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z"
              />
            </svg>
          </button>
        )}
        {canDelete && allowMutations && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(data);
            }}
            className="text-red-600 hover:text-red-800 transform hover:scale-110 transition-all"
            title="Delete Season"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        )}
      </div>
    );
  };

  const columnDefs: ColDef<SeasonData>[] = [
    {
      field: 'name',
      headerName: 'Name',
      sortable: true,
      flex: 2,
      minWidth: 220,
      cellRenderer: NameCellRenderer,
    },
    {
      headerName: 'Type',
      field: 'isTournament',
      sortable: true,
      flex: 1,
      minWidth: 120,
      cellRenderer: TypeCellRenderer,
      valueGetter: (params) => (params.data?.isTournament ? 'Tournament' : 'Season'),
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      sortable: true,
      flex: 1,
      minWidth: 120,
      cellRenderer: DateCellRenderer,
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      sortable: true,
      flex: 1,
      minWidth: 120,
      cellRenderer: DateCellRenderer,
    },
    {
      headerName: 'Shot Clock',
      field: 'shotClock',
      sortable: true,
      flex: 1,
      cellRenderer: ShotClockCellRenderer,
      minWidth: 100,
    },
    {
      headerName: 'Period',
      field: 'halfPeriod',
      sortable: true,
      flex: 1,
      minWidth: 110,
      cellRenderer: PeriodCellRenderer,
      valueGetter: (params) => (params.data?.halfPeriod ? 'Halves' : 'Quarters'),
    },
    {
      headerName: 'Status',
      field: 'active',
      sortable: true,
      flex: 1,
      cellRenderer: StatusCellRenderer,
      minWidth: 100,
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      width: 100,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 100,
      },
    },
  ];

  useEffect(() => {
    return () => {
      setIsModalOpen(false);
      setIsDeleteDialogOpen(false);
      setSeasonToDelete(null);
      setEditSeason(null);
      if (gridApi.current) {
        gridApi.current.purgeInfiniteCache();
      }
    };
  }, []);

  return (
    <div className="w-full p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl xss:text-2xl font-bold">Seasons</h1>
        <PermissionGuard resource="seasons" action="add">
          <button
            onClick={() => {
              if (!clientIdFromSession) {
                showErrorMsg('Please select a client before adding a season.');
                return;
              }
              setEditSeason(null);
              setIsModalOpen(true);
            }}
            className="bg-[#4eea4d] text-black font-semibold px-4 py-1 rounded hover:bg-[#41ca41] transition-colors shadow-2xl"
          >
            Add Season
          </button>
        </PermissionGuard>
      </div>

      <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowModelType="infinite"
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[25, 50, 100]}
          cacheBlockSize={pageSize}
          datasource={datasource}
          onGridReady={onGridReady}
          onPaginationChanged={onPaginationChanged}
          onRowClicked={handleRowClick}
          rowStyle={{ cursor: 'pointer' }}
          loading={isLoading}
          blockLoadDebounceMillis={50}
          maxBlocksInCache={2}
          infiniteInitialRowCount={pageSize}
          overlayLoadingTemplate={
            '<div role="status"><svg aria-hidden="true" class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/></svg><span class="sr-only">Loading...</span></div>'
          }
          overlayNoRowsTemplate={'<span class="ag-overlay-no-rows-center">No seasons found</span>'}
        />
      </div>

      {isModalOpen && (
        <AddSeasonModal onClose={handleModalClose} onSeasonAdded={handleSeasonAddedOrUpdated} editSeason={editSeason} />
      )}

      {isDeleteDialogOpen && (
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Season"
          message={`Are you sure you want to delete ${seasonToDelete?.name}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}
    </div>
  );
};

export default Seasons;
